import { ethers } from "ethers";
import { formatUnits } from "viem";
import { useEffect, useMemo, useState } from "react";
import { useAccount, useReadContracts } from "wagmi";
import { useInterval, useIdle } from "react-use";
import { usePathname } from "next/navigation";
import { differenceInCalendarDays } from "date-fns";
import useDappChain from "@hooks/useDappChain";

const HOURS_TO_ADD = 16;
const DAYS_TO_ADD = 4;
const POLLING_INTERVAL = 10000;

const provider = new ethers.providers.JsonRpcProvider(
  "https://eth-mainnet.g.alchemy.com/v2/iuphmpG3ftqu_yNyvpDsFtukkzZbyqDK"
);

interface VaultClaimsConfig {
  // Contract configuration
  contractConfig: any; // vaultsContractConfig or yieldVaultContractConfig
  abi: any; // ABI for the contract
  enabledChains: Array<{ id: number }>;

  // Optional overrides
  contractAddress?: string;

  // Path patterns for polling enablement
  pathPatterns?: string[];
}

const getClaims = async (
  contract: ethers.Contract,
  walletAddr: string,
  abi: any,
  contractAddress: string
) => {
  const iface = new ethers.utils.Interface(abi);

  const filter = {
    address: contractAddress,
    topics: [iface.getEventTopic("WithdrawalRequested")],
  };

  const logs = await contract.queryFilter(filter);
  const events = logs.map((log) => iface.parseLog(log));

  const availableRedemptions: Record<
    string,
    Number | Date | string | boolean
  >[] = [];

  events.forEach((ev) => {
    if (!ev?.args) return;
    const [, receiverAddr, , assets, , bnYear, bnMonth, bnDay] = ev.args;

    if (receiverAddr && receiverAddr === walletAddr) {
      const [year, month, day] = [
        bnYear.toNumber(),
        bnMonth.toNumber(),
        bnDay.toNumber(),
      ];

      const str = `${String(bnYear).padStart(2, "0")}-${String(
        bnMonth
      ).padStart(2, "0")}-${String(bnDay).padStart(2, "0")}T00:00:00Z`;

      const claimDate = new Date(str);

      const claimableDateTime =
        claimDate.getTime() +
        (DAYS_TO_ADD * 24 + HOURS_TO_ADD) * 60 * 60 * 1000;
      const updatedclaimableDate = new Date(claimableDateTime);

      availableRedemptions.push({
        isClaimable: Date.now() > updatedclaimableDate.getTime(),
        claimableTime: `~ in ${
          Math.abs(
            differenceInCalendarDays(updatedclaimableDate.getTime(), Date.now())
          ) + 1
        } days`,
        receiver: receiverAddr,
        day,
        month,
        year,
        amount: formatUnits(assets, 18),
        date: claimDate,
        index: availableRedemptions.length + 1,
        key: `${day}-${month}-${year}`,
      });
    }
  });

  const redemptionByDay = availableRedemptions.reduce((prev: any, data) => {
    const key = data.key.toString();
    if (prev[data.key as string]) {
      const upatedAmount = +prev[key].amount + +data.amount;
      prev[key] = {
        ...data,
        amount: upatedAmount.toString(),
      };
    } else {
      prev[key] = {
        ...data,
      };
    }
    return prev;
  }, {});

  return Object.values(redemptionByDay);
};

const useVaultClaims = (config: VaultClaimsConfig) => {
  const isIdle = useIdle(150e3);
  const { isConnected, address } = useAccount();
  const { chain } = useDappChain();

  const pathname = usePathname();

  const isVaultPage = useMemo(() => {
    if (!config.pathPatterns || config.pathPatterns.length === 0) {
      return pathname.includes("/gain") || pathname.includes("/vault");
    }
    return config.pathPatterns.some((pattern) => pathname.includes(pattern));
  }, [pathname, config.pathPatterns]);

  const [claimList, setClaimList] = useState<any>([]);

  // Determine contract address
  const contractAddr = useMemo(() => {
    if (config.contractAddress) {
      return config.contractAddress;
    }

    const chainConfig = config.contractConfig[chain.id];
    if (chainConfig?.interactionPool?.address) {
      return chainConfig.interactionPool.address;
    }

    // Fallback to first enabled chain
    if (config.enabledChains.length > 0) {
      const fallbackChainId = config.enabledChains[0].id;
      return config.contractConfig[fallbackChainId]?.interactionPool?.address;
    }

    return undefined;
  }, [config.contractAddress, config.contractConfig, chain.id, config.enabledChains]);

  const poolContract = useMemo(() => {
    if (!contractAddr) return null;
    return new ethers.Contract(contractAddr, config.abi, provider);
  }, [contractAddr, config.abi]);

  const { data: availableClaimBalances, refetch } = useReadContracts({
    contracts: claimList.map((data: any) => ({
      address: contractAddr as `0x${string}`,
      abi: config.abi,
      functionName: "getClaimableAmountByReceiver",
      args: [data.year, data.month, data.day, data.receiver],
    })),
    query: {
      enabled: !isIdle && !!address && claimList?.length > 0 && !!contractAddr,
    },
  });

  const fetchClaims = async () => {
    if (address && poolContract && contractAddr) {
      const claims = await getClaims(poolContract, address, config.abi, contractAddr);
      setClaimList(claims || []);
    }
  };

  useEffect(() => {
    refetch();
  }, [claimList, refetch]);

  useEffect(() => {
    if (isConnected && address && contractAddr) {
      fetchClaims();
    } else {
      setClaimList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, address, chain.id, contractAddr]);

  useInterval(() => {
    if (!isIdle && isVaultPage) {
      fetchClaims();
    }
  }, POLLING_INTERVAL);

  // If no claims from blockchain events, return empty
  if (claimList.length === 0) {
    return [];
  }

  // Map claims with updated balances from contract
  const mappedClaims = claimList.map((data: any, index: number) => {
    return {
      ...data,
      amount:
        availableClaimBalances?.[index]?.result !== undefined
          ? formatUnits(availableClaimBalances[index].result as bigint, 18)
          : data.amount,
    };
  });

  // Only filter by amount > 0 after we have contract balance data
  if (availableClaimBalances) {
    return mappedClaims.filter((data: { amount: number }) => data.amount > 0);
  }

  // While loading balances, return empty to avoid showing invalid claims
  return [];
};

export default useVaultClaims;
